import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Product } from './index';
import { categoryService } from '../../services/categoryService';
import { productService } from '../../services/productService';
import './RelatedProducts.css';

interface RelatedProductsProps {
  currentProductId: string;
  category: string;
  language: string;
}

const RelatedProducts: React.FC<RelatedProductsProps> = ({
  currentProductId,
  category,
  language
}) => {
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use the product service instead of direct fetch
        const products = await productService.getRelatedProducts(currentProductId, category, 5);
        setRelatedProducts(products);
      } catch (err) {
        console.error('Error fetching related products:', err);
        setError('Failed to load related products');
        setRelatedProducts([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    if (currentProductId && category) {
      fetchRelatedProducts();
    }
  }, [currentProductId, category]);

  // Don't render if no products or error
  if (loading && relatedProducts.length === 0) {
    return (
      <div className="related-products-section">
        <div className="related-products-loading">
          <div className="loading-spinner"></div>
          <p>{language === 'ar' ? 'جاري تحميل المنتجات ذات الصلة...' : 'Loading related products...'}</p>
        </div>
      </div>
    );
  }

  if (error || relatedProducts.length === 0) {
    return null; // Don't show anything if there are no related products
  }

  const categoryName = categoryService.getCategoryName(category, language as 'en' | 'ar');

  return (
    <div className="related-products-section">
      <div className="related-products-container">
        <h3 className="related-products-title">
          <span className="ar-content">
            {language === 'ar' ? `منتجات أخرى في ${categoryName}` : ''}
          </span>
          <span className="en-content">
            {language === 'en' ? `More in ${categoryName}` : ''}
          </span>
        </h3>

        <div className="related-products-grid">
          {relatedProducts.map((product) => (
            <Link
              key={product.id}
              to={`/shop/product/${product.id}`}
              className="related-product-card"
            >
              <div className="related-product-image">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images.find(img => img.isMain)?.url || product.images[0].url}
                    alt={product.images[0].alt}
                    loading="lazy"
                  />
                ) : (
                  <div className="related-product-placeholder">
                    {language === 'ar' ? 'لا توجد صورة' : 'No Image'}
                  </div>
                )}
              </div>

              <div className="related-product-info">
                <h4 className="related-product-title">
                  {language === 'ar' ? product.titleAr : product.titleEn}
                </h4>

                <p className="related-product-price">{product.price} ₪</p>

                {product.ageRangeEn && (
                  <p className="related-product-age">
                    <span className="ar-content">
                      {language === 'ar' ? `العمر: ${product.ageRangeAr}` : ''}
                    </span>
                    <span className="en-content">
                      {language === 'en' ? `Age: ${product.ageRangeEn}` : ''}
                    </span>
                  </p>
                )}

                <div className="related-product-cta">
                  {language === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                </div>
              </div>

              {product.featured && (
                <div className="related-product-badge">
                  {language === 'ar' ? 'مميز' : 'Featured'}
                </div>
              )}
            </Link>
          ))}
        </div>

        <div className="related-products-footer">
          <Link
            to={`/shop?category=${category}`}
            className="view-all-category-btn"
          >
            <span className="ar-content">
              {language === 'ar' ? `عرض جميع منتجات ${categoryName}` : ''}
            </span>
            <span className="en-content">
              {language === 'en' ? `View all ${categoryName} products` : ''}
            </span>
            <span className="arrow-icon">
              {language === 'ar' ? '←' : '→'}
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RelatedProducts;